"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
const query_string_1 = require("query-string");
var HttpMethod;
(function (HttpMethod) {
    HttpMethod["GET"] = "GET";
    HttpMethod["POST"] = "POST";
    HttpMethod["PUT"] = "PUT";
    HttpMethod["PATCH"] = "PATCH";
    HttpMethod["DELETE"] = "DELETE";
    HttpMethod["HEAD"] = "HEAD";
    HttpMethod["OPTIONS"] = "OPTIONS";
})(HttpMethod = exports.HttpMethod || (exports.HttpMethod = {}));
class ApiCallImpl {
    constructor(path, method, query, bearerToken, contentType, headers, body) {
        this.path = path;
        this.method = method;
        this.query = query;
        this.bearerToken = bearerToken;
        this.contentType = contentType;
        this.headers = headers;
        this.body = body;
        this.consumed = false;
        this.onResponseCbs = [];
        this.onJsonResponseCbs = [];
        this.then = (onfulfilled, onrejected) => {
            if (this.consumed) {
                return Promise.reject(new Error('Called ApiCall twice! Re-use the promise instead of making multiple requests.'));
            }
            this.consumed = true;
            const _a = this.build(), { path } = _a, options = __rest(_a, ["path"]);
            return fetch(path, options).then(response => {
                return this.onResponseCbs
                    .reduce((acc, cb) => acc.then(() => cb(response)), Promise.resolve())
                    .then(() => response)
                    .then(onfulfilled, onrejected);
            });
        };
        this.catch = async (onrejected) => {
            return this.then().catch(onrejected);
        };
        this.finally = async (onfinally) => {
            return this.then().finally(onfinally);
        };
    }
    applyTransforms(transforms) {
        return transforms.reduce((call, transform) => {
            if (Array.isArray(transform)) {
                const [method, wrap] = transform;
                if (method === this.method)
                    return wrap(call);
                return call;
            }
            return transform(call);
        }, this);
    }
    withQuery(query, options) {
        this.query = query;
        this.queryOptions = options;
        return this;
    }
    withBearerToken(token) {
        this.bearerToken = token;
        return this;
    }
    withContentType(contentType) {
        this.contentType = contentType;
        return this;
    }
    withHeaders(headers) {
        this.headers = headers;
        return this;
    }
    withHeader(name, value) {
        if (!this.headers)
            this.headers = new Headers();
        this.headers.set(name, value);
        return this;
    }
    withBody(body, json = true, options) {
        this.body = body;
        this.bodyOptions = options;
        if (json)
            return this.withContentType('application/json');
        return this;
    }
    expectStatus(code) {
        return this.onResponse(res => {
            if (res.status !== code) {
                throw new Error(`Expected ${code} response, got ${res.status}`);
            }
        });
    }
    onResponse(cb) {
        this.onResponseCbs.push(cb);
        return this;
    }
    onJsonResponse(cb) {
        this.onJsonResponseCbs.push(cb);
        return this;
    }
    build() {
        var _a;
        const headers = (_a = this.headers, (_a !== null && _a !== void 0 ? _a : new Headers()));
        if (this.bearerToken && this.bearerToken.token) {
            headers.set('authorization', `Bearer ${this.bearerToken.token}`);
        }
        if (this.contentType) {
            headers.set('content-type', this.contentType);
        }
        let path;
        if (this.query) {
            path = `${this.path}?${query_string_1.stringify(applySerializationOptions(this.query, this.queryOptions))}`;
        }
        else {
            path = this.path;
        }
        let body;
        if (this.body && this.contentType === 'application/json') {
            body = JSON.stringify(applySerializationOptions(this.body, this.bodyOptions));
        }
        else {
            body = this.body;
        }
        return {
            path,
            headers,
            method: this.method,
            body,
        };
    }
    get [Symbol.toStringTag]() {
        return 'ApiCall';
    }
    async json() {
        return this.jsonAndResponse().then(([json]) => json);
    }
    async jsonAndResponse() {
        return this.then().then(async (res) => {
            const json = await res.json();
            for (const cb of this.onJsonResponseCbs)
                await cb(json, res);
            return [json, res];
        });
    }
    async blob() {
        return this.blobAndResponse().then(([blob]) => blob);
    }
    async blobAndResponse() {
        return this.then().then(async (res) => [await res.blob(), res]);
    }
}
exports.buildPath = (...args) => {
    // https://stackoverflow.com/a/46427607/1165996
    return args
        .map((part, i) => {
        if (i === 0) {
            return part.trim().replace(/[/]*$/g, '');
        }
        return part.trim().replace(/(^[/]*|[/]*$)/g, '');
    })
        .filter(x => x.length)
        .join('/');
};
exports.api = (baseURL, transforms = []) => {
    const call = (path, method) => {
        return new ApiCallImpl(exports.buildPath(baseURL, path), method).applyTransforms(transforms);
    };
    const withTransform = (t) => {
        return exports.api(baseURL, transforms.concat([t]));
    };
    const withBearerToken = (token) => {
        return withTransform(c => c.withBearerToken(token));
    };
    const onResponse = (cb) => {
        return withTransform(c => c.onResponse(cb));
    };
    const onJsonResponse = (cb) => {
        return withTransform(c => c.onJsonResponse(cb));
    };
    return {
        call,
        withTransform,
        withBearerToken,
        onResponse,
        onJsonResponse,
        get: path => call(path, HttpMethod.GET),
        post: path => call(path, HttpMethod.POST),
        put: path => call(path, HttpMethod.PUT),
        patch: path => call(path, HttpMethod.PATCH),
        delete: path => call(path, HttpMethod.DELETE),
        head: path => call(path, HttpMethod.HEAD),
        options: path => call(path, HttpMethod.OPTIONS),
        changeBaseURL: path => {
            baseURL = path;
        },
    };
};
exports.apiCall = (path, method) => {
    return new ApiCallImpl(path, method);
};
function applySerializationOptions(obj, options = {}) {
    var _a;
    const output = Object.assign({}, obj);
    if ((_a = options) === null || _a === void 0 ? void 0 : _a.stripEmptyStrings) {
        for (const [key, val] of Object.entries(output)) {
            if (val === '') {
                delete output[key];
            }
        }
    }
    return output;
}
//# sourceMappingURL=index.js.map